import classnames from 'classnames';
import { isEqual } from 'lodash/fp';
import React, { MouseEvent, useCallback, useMemo } from 'react';

import { DatasetSection, DocumentSpecificHiddenFields, OpenFieldType } from '../../../datasets/store';
import { Icon } from '../../icon/Icon';
import { CaretDown, CaretSide, Tick, WarningSign } from '../../icons';
import { OverflowTooltip, CustomTooltip } from '../../tooltip';
import { DocumentNameDB } from '../../../admin/documents/store';
import { DatasetFieldSection, RiskField, SelectedRiskField } from '../../../admin/risk-tolerance/store';
import { useSingleHiddenDocumentFields } from '../../../../hooks/useHiddenSingleFields';
import { fieldListIcons } from '../../../admin/dataset-builder/FieldList';
import { getRiskToleranceConfigIsEmpty } from '../../../admin/risk-tolerance/utils';
import { RadioButton } from '../../button/RadioButton';
import styles from '../RiskTolerance.module.scss';

const { amber, disabled, primary } = styles;

interface SectionMenuProps {
    parentDatasetId: number;
    getDatasetFields: (datasetId: number, sectionId: string | null) => DatasetFieldSection[];
    sectionId: string | null;
    datasetSectionOpen: (datasetId: number) => boolean;
    getDatasetRiskFields: (datasetId: number, sectionId: string | null) => RiskField[];
    selectRiskField: (fieldId: string, datasetId: number) => void;
    selectedField: SelectedRiskField | null;
    toggleDatasetOpen: (datasetId: number) => void;
    getDatasetSections: (datasetId: number) => DatasetSection[];
    getSectionOpen: (sectionId: string | null, datasetId: number) => boolean;
    toggleSectionOpen: (sectionId: string | null, datasetId: number) => void;
    depth: number;
    showRiskFieldToggles: boolean;
    toggleRiskField: (datasetId: number, fieldId: string) => void;
    documentHiddenFields: DocumentSpecificHiddenFields;
    allDocumentNames: DocumentNameDB[];
    agreementTypeDatasetId: number | null;
    isAnalyticsMenu: boolean;
}

export const SectionMenu: React.FC<SectionMenuProps> = ({
    parentDatasetId,
    getDatasetFields,
    sectionId,
    datasetSectionOpen,
    getDatasetRiskFields,
    selectedField,
    toggleDatasetOpen,
    selectRiskField,
    getDatasetSections,
    getSectionOpen,
    toggleSectionOpen,
    depth,
    showRiskFieldToggles,
    toggleRiskField,
    documentHiddenFields,
    allDocumentNames,
    agreementTypeDatasetId,
    isAnalyticsMenu
}) => {
    const sectionOpenIcon = useCallback((datasetId: number) => datasetSectionOpen(datasetId) ? CaretDown : CaretSide, [datasetSectionOpen]);
    const isSelectedRiskField = useCallback((fieldId: string, datasetId: number) => selectedField && isEqual({ fieldId, datasetId }, selectedField), [selectedField]);
    const datasetFields = useMemo(() => getDatasetFields(parentDatasetId, sectionId), [getDatasetFields, parentDatasetId, sectionId]);
    const datasetRiskFields = useMemo(() => getDatasetRiskFields(parentDatasetId, sectionId), [getDatasetRiskFields, parentDatasetId, sectionId]);
    const datasetWrapperStyle = useMemo(() => ({ width: `calc(100% - ${depth * 5}px`, paddingLeft: `${depth * 5}px` }), [depth]);

    const datasetFieldsMenu = useMemo(() => {
        if (datasetFields.length === 1) {
            const datasetId = parseInt(datasetFields[0].field.settings.datasetLinked!);
            return (
                <DatasetMenu
                    parentDatasetId={datasetId}
                    getDatasetFields={getDatasetFields}
                    datasetSectionOpen={datasetSectionOpen}
                    getDatasetRiskFields={getDatasetRiskFields}
                    selectedField={selectedField}
                    toggleDatasetOpen={toggleDatasetOpen}
                    selectRiskField={selectRiskField}
                    getDatasetSections={getDatasetSections}
                    getSectionOpen={getSectionOpen}
                    toggleSectionOpen={toggleSectionOpen}
                    depth={depth + 1}
                    showRiskFieldToggles={showRiskFieldToggles}
                    toggleRiskField={toggleRiskField}
                    documentHiddenFields={documentHiddenFields}
                    allDocumentNames={allDocumentNames}
                    agreementTypeDatasetId={agreementTypeDatasetId}
                    isAnalyticsMenu={isAnalyticsMenu}
                />
            );
        }

        return (
            datasetFields.map(({ field: { label, id, settings: { datasetLinked } } }) => {
                const datasetId = parseInt(datasetLinked!);
                return (
                    <div className={styles.datasetWrapper} key={id} style={datasetWrapperStyle}>
                        <div className={styles.datasetHeader} onClick={() => toggleDatasetOpen(datasetId)} data-testid={`risk-tolerance-dataset-${datasetId}`}>
                            <div className={styles.datasetOpenIcon}>
                                <Icon icon={sectionOpenIcon(datasetId)} fontSize={15} />
                            </div>
                            <OverflowTooltip overlayText={label} />
                        </div>
                        {datasetSectionOpen(datasetId) &&
                            <DatasetMenu
                                parentDatasetId={datasetId}
                                getDatasetFields={getDatasetFields}
                                datasetSectionOpen={datasetSectionOpen}
                                getDatasetRiskFields={getDatasetRiskFields}
                                selectedField={selectedField}
                                toggleDatasetOpen={toggleDatasetOpen}
                                selectRiskField={selectRiskField}
                                getDatasetSections={getDatasetSections}
                                getSectionOpen={getSectionOpen}
                                toggleSectionOpen={toggleSectionOpen}
                                depth={depth + 1}
                                showRiskFieldToggles={showRiskFieldToggles}
                                toggleRiskField={toggleRiskField}
                                documentHiddenFields={documentHiddenFields}
                                allDocumentNames={allDocumentNames}
                                agreementTypeDatasetId={agreementTypeDatasetId}
                                isAnalyticsMenu={isAnalyticsMenu}
                            />
                        }
                    </div>
                );
            })
        );
    }, [
        datasetFields,
        toggleDatasetOpen,
        sectionOpenIcon,
        getDatasetFields,
        datasetSectionOpen,
        getDatasetRiskFields,
        selectedField,
        selectRiskField,
        getDatasetSections,
        getSectionOpen,
        toggleSectionOpen,
        depth,
        datasetWrapperStyle,
        showRiskFieldToggles,
        toggleRiskField,
        documentHiddenFields,
        allDocumentNames,
        agreementTypeDatasetId,
        isAnalyticsMenu
    ]);

    const LABEL_MARGIN = 10;
    const ICON_WIDTH = 32;
    const RADIO_WIDTH = 18;
    const EMPTY_WARNING_WIDTH = 20;

    const labelWidth = useMemo(() => LABEL_MARGIN + ICON_WIDTH + (showRiskFieldToggles ? RADIO_WIDTH : 0), [showRiskFieldToggles]);

    const toggleField = useCallback((e: MouseEvent<HTMLElement>, datasetId: number, fieldId: string) => {
        e.stopPropagation();
        toggleRiskField(datasetId, fieldId);
    }, [toggleRiskField]);

    const documentSpecificHiddenFields = useMemo(() => documentHiddenFields[parentDatasetId] || [], [parentDatasetId, documentHiddenFields]);
    const documentHiddenSingleFields = useSingleHiddenDocumentFields(documentSpecificHiddenFields);

    const getFieldHiddenForAllDatasetDocuments = useCallback((id: string) => {
        const documentsHiddenForField = documentHiddenSingleFields.find(({ fieldId }) => fieldId === id)?.documentNameIds || [];
        const documentNameIdsForDataset = allDocumentNames.filter(({ datasetId }) => datasetId === agreementTypeDatasetId).map(({ documentNameId }) => documentNameId!);
        return documentsHiddenForField.length > 0 && documentNameIdsForDataset.length > 0 && documentNameIdsForDataset.every(documentNameId => documentsHiddenForField.includes(documentNameId));
    }, [allDocumentNames, documentHiddenSingleFields, agreementTypeDatasetId]);

    return (
        <div>
            <div className={styles.datasetRiskFields}>
                {datasetRiskFields.map(riskField => {
                    const { fieldId, label, type, includeRiskField } = riskField;
                    const fieldHiddenForAllDatasetDocuments = getFieldHiddenForAllDatasetDocuments(fieldId);
                    const isEmptyConfig = getRiskToleranceConfigIsEmpty(riskField, type);
                    if (fieldHiddenForAllDatasetDocuments) {
                        return null;
                    }
                    return (
                        <CustomTooltip key={fieldId} overlayText={isEmptyConfig && isAnalyticsMenu ? 'A risk tolerance has not been configured for this field.' : ''}>
                            <div onClick={() => selectRiskField(fieldId, parentDatasetId)} className={classnames(styles.riskFieldWrapper, { [styles.selectedRiskField]: isSelectedRiskField(fieldId, parentDatasetId), [styles.disabledRiskField]: (isEmptyConfig && isAnalyticsMenu) })} key={fieldId}>
                                <div className={styles.riskFieldLabel} style={{ width: `calc(100% - ${labelWidth + (isEmptyConfig ? EMPTY_WARNING_WIDTH : 0)}px)` }}>
                                    <OverflowTooltip overlayText={label} />
                                </div>
                                {showRiskFieldToggles && <RadioButton fontSize={10} withBackground={includeRiskField} onClick={e => toggleField(e, parentDatasetId, fieldId)} icon={Tick} />}
                                {isEmptyConfig && isAnalyticsMenu && <Icon icon={WarningSign} color={amber} fontSize={20} />}
                                <div className={styles.riskFieldType}><Icon icon={fieldListIcons[type]} color={(isEmptyConfig && isAnalyticsMenu) ? disabled : primary} /></div>
                            </div>
                        </CustomTooltip>
                    );
                }
                )}
            </div>
            {datasetFieldsMenu}
        </div>
    );
};

interface DatasetMenuProps {
    parentDatasetId: number;
    getDatasetFields: (datasetId: number, sectionId: string | null) => DatasetFieldSection[];
    getDatasetSections: (datasetId: number) => DatasetSection[];
    datasetSectionOpen: (datasetId: number) => boolean;
    getDatasetRiskFields: (datasetId: number, sectionId: string | null) => RiskField[];
    selectRiskField: (fieldId: string, datasetId: number) => void;
    selectedField: SelectedRiskField | null;
    toggleDatasetOpen: (datasetId: number) => void;
    getSectionOpen: (sectionId: string | null, datasetId: number) => boolean;
    toggleSectionOpen: (sectionId: string | null, datasetId: number) => void;
    depth: number;
    showRiskFieldToggles: boolean;
    toggleRiskField: (datasetId: number, fieldId: string) => void;
    documentHiddenFields: DocumentSpecificHiddenFields;
    allDocumentNames: DocumentNameDB[];
    agreementTypeDatasetId: number | null;
    isAnalyticsMenu: boolean;
}

export const DatasetMenu: React.FC<DatasetMenuProps> = ({
    parentDatasetId,
    getDatasetFields,
    getDatasetSections,
    datasetSectionOpen,
    getDatasetRiskFields,
    selectedField,
    toggleDatasetOpen,
    selectRiskField,
    getSectionOpen,
    toggleSectionOpen,
    depth,
    showRiskFieldToggles,
    toggleRiskField,
    documentHiddenFields,
    allDocumentNames,
    agreementTypeDatasetId,
    isAnalyticsMenu
}) => {
    const sectionOpenIcon = useCallback((sectionId: string) => getSectionOpen(sectionId, parentDatasetId) ? CaretDown : CaretSide, [getSectionOpen, parentDatasetId]);
    const sections = useMemo(() => getDatasetSections(parentDatasetId), [getDatasetSections, parentDatasetId]);
    const sectionStyle = { width: `calc(100% - ${depth * 5}px`, paddingLeft: `${depth * 5}px` };

    if (sections.length > 1) {
        return (
            <div className={styles.sectionMenuWrapper} style={sectionStyle}>
                {sections.map(({ id, label }) => {
                    const documentSpecificHiddenFields = documentHiddenFields[parentDatasetId] || [];
                    const currentHiddenDocumentsForSection = documentSpecificHiddenFields.filter(({ type }) => type === OpenFieldType.SECTION).find(({ sectionId }) => sectionId === id)?.documentNameIds || [];
                    const documentNameIdsForDataset = allDocumentNames.filter(({ datasetId }) => datasetId === agreementTypeDatasetId).map(({ documentNameId }) => documentNameId!);
                    const sectionHiddenForAllDatasetDocuments = currentHiddenDocumentsForSection.length > 0 && documentNameIdsForDataset.every(documentNameId => currentHiddenDocumentsForSection.includes(documentNameId));
                    if (sectionHiddenForAllDatasetDocuments) {
                        return null;
                    }
                    return (
                        <div className={styles.sectionWrapper} key={id}>
                            <div className={styles.sectionHeader} onClick={() => toggleSectionOpen(id, parentDatasetId)} data-testid={`risk-tolerance-dataset-section-${id}`}>
                                <div className={styles.sectionOpenIcon}>
                                    <Icon icon={sectionOpenIcon(id)} fontSize={15} />
                                </div>
                                <OverflowTooltip overlayText={label} />
                            </div>
                            {getSectionOpen(id, parentDatasetId) &&
                                <SectionMenu
                                    sectionId={id}
                                    parentDatasetId={parentDatasetId}
                                    getDatasetFields={getDatasetFields}
                                    datasetSectionOpen={datasetSectionOpen}
                                    getDatasetRiskFields={getDatasetRiskFields}
                                    selectedField={selectedField}
                                    toggleDatasetOpen={toggleDatasetOpen}
                                    selectRiskField={selectRiskField}
                                    getDatasetSections={getDatasetSections}
                                    getSectionOpen={getSectionOpen}
                                    toggleSectionOpen={toggleSectionOpen}
                                    depth={depth}
                                    showRiskFieldToggles={showRiskFieldToggles}
                                    toggleRiskField={toggleRiskField}
                                    documentHiddenFields={documentHiddenFields}
                                    allDocumentNames={allDocumentNames}
                                    agreementTypeDatasetId={agreementTypeDatasetId}
                                    isAnalyticsMenu={isAnalyticsMenu}
                                />
                            }
                        </div>
                    );
                })}
            </div>
        );
    }
    return (
        <SectionMenu
            sectionId={sections[0] ? sections[0].id : null}
            parentDatasetId={parentDatasetId}
            getDatasetFields={getDatasetFields}
            datasetSectionOpen={datasetSectionOpen}
            getDatasetRiskFields={getDatasetRiskFields}
            selectedField={selectedField}
            toggleDatasetOpen={toggleDatasetOpen}
            selectRiskField={selectRiskField}
            getDatasetSections={getDatasetSections}
            getSectionOpen={getSectionOpen}
            toggleSectionOpen={toggleSectionOpen}
            depth={depth}
            showRiskFieldToggles={showRiskFieldToggles}
            toggleRiskField={toggleRiskField}
            documentHiddenFields={documentHiddenFields}
            allDocumentNames={allDocumentNames}
            agreementTypeDatasetId={agreementTypeDatasetId}
            isAnalyticsMenu={isAnalyticsMenu}
        />
    );
};
